import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useHiddenMode } from '../../contexts/HiddenModeContext';
import { menuConfig, MenuItem } from '../../config/menuConfig';
import { filterMenuByPermissions } from '../../utils/menuPermissions';
import './NavigationMenu.css';

interface NavigationMenuProps {
  isCollapsed?: boolean;
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({ isCollapsed = false }) => {
  const { user } = useAuth();
  const { isHiddenModeActive } = useHiddenMode();
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);

  const filterHiddenItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter(item => {
      if (item.isHidden && !isHiddenModeActive) {
        return false;
      }
      if (item.children) {
        const filteredChildren = filterHiddenItems(item.children);
        return filteredChildren.length > 0;
      }
      return true;
    }).map(item => ({
      ...item,
      children: item.children ? filterHiddenItems(item.children) : undefined
    }));
  };

  const permissionFilteredMenuItems = user ? 
    filterMenuByPermissions(menuConfig, user.allPermissions || []) : 
    [];
  
  const accessibleMenuItems = filterHiddenItems(permissionFilteredMenuItems);

  React.useEffect(() => {
    if (user && (user.allPermissions?.includes('*') || user.allPermissions?.includes('superadmin'))) {
      console.log('🔍 NAVIGATION MENU DEBUG for Super Admin:');
      console.log('   Total permissions:', user.allPermissions?.length || 0);
      console.log('   Has * permission:', user.allPermissions?.includes('*'));
      console.log('   Has superadmin permission:', user.allPermissions?.includes('superadmin'));
      console.log('   Original menu config items:', menuConfig.length);
      console.log('   After permission filter:', permissionFilteredMenuItems.length);
      console.log('   After hidden filter:', accessibleMenuItems.length);
      console.log('   Hidden mode active:', isHiddenModeActive);
      console.log('   Accessible menu items:', accessibleMenuItems.map(item => ({
        key: item.key,
        label: item.label,
        hasChildren: !!item.children,
        childrenCount: item.children?.length || 0
      })));
    }
  }, [user, permissionFilteredMenuItems, accessibleMenuItems, isHiddenModeActive]);

  const toggleMenu = (menuKey: string) => {
    const newExpanded = new Set(expandedMenus);
    if (newExpanded.has(menuKey)) {
      newExpanded.delete(menuKey);
    } else {
      newExpanded.add(menuKey);
    }
    setExpandedMenus(newExpanded);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const hasActiveChild = (children?: MenuItem[]): boolean => {
    if (!children) return false;
    return children.some(child => isActive(child.path) || hasActiveChild(child.children));
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.has(item.key);
    const itemIsActive = isActive(item.path) || hasActiveChild(item.children);
    const isHovered = hoveredMenu === item.key;

    if (!item.path && hasChildren) {
      return (
        <li
          key={item.key}
          className={`menu-item level-${level} has-children ${itemIsActive ? 'active-branch' : ''} ${isHovered ? 'hovered' : ''}`}
          onMouseEnter={() => isCollapsed && setHoveredMenu(item.key)}
          onMouseLeave={() => isCollapsed && setHoveredMenu(null)}
        >
          <div
            className={`menu-header ${hasChildren ? 'expandable' : ''} ${itemIsActive ? 'active' : ''}`}
            onClick={!isCollapsed && hasChildren ? () => toggleMenu(item.key) : undefined}
          >
            {item.icon && <span className="menu-icon">{item.icon}</span>}
            <span className="menu-label">{item.label}</span>
            {hasChildren && !isCollapsed && (
              <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                ▼
              </span>
            )}
          </div>

          {hasChildren && isExpanded && !isCollapsed && (
            <ul className="submenu">
              {item.children!.map(child => renderMenuItem(child, level + 1))}
            </ul>
          )}

          {hasChildren && isCollapsed && isHovered && (
            <div className="collapsed-popover">
              <div className="popover-label">{item.label}</div>
              <ul className="popover-submenu">
                {item.children!.map(child => (
                  <li key={child.key}>
                    <Link
                      to={child.path || '#'}
                      className={`popover-link ${isActive(child.path) ? 'active' : ''}`}
                    >
                      {child.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      );
    }

    if (item.path && !hasChildren) {
      return (
        <li
          key={item.key}
          className={`menu-item level-${level} ${itemIsActive ? 'active-branch' : ''}`}
        >
          <Link
            to={item.path}
            className={`menu-link ${itemIsActive ? 'active' : ''}`}
            title={isCollapsed ? item.label : undefined}
          >
            {item.icon && <span className="menu-icon">{item.icon}</span>}
            <span className="menu-label">{item.label}</span>
          </Link>
        </li>
      );
    }

    return null;
  };

  if (!user) {
    return null;
  }

  return (
    <nav className={`navigation-menu ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="menu-header-section">
        <h3>{isCollapsed ? '' : 'ACSW Dashboard'}</h3>
        <div className="user-info">
          <span className="user-name">{isCollapsed ? '' : user.employeeName}</span>
        </div>
      </div>
      
      <ul className="menu-list">
        {accessibleMenuItems.map(item => renderMenuItem(item))}
      </ul>
      
      {accessibleMenuItems.length === 0 && (
        <div className="no-access-message">
          <p>No accessible menu items found.</p>
          <p>Please contact your administrator.</p>
        </div>
      )}
    </nav>
  );
};

export default NavigationMenu;
